/** @typedef {'invalidPrincipal' | 'invalidRate' | 'invalidTenure'} EmiErrorCode */

const MIN_PRINCIPAL = 1;
const MAX_PRINCIPAL = 100_000_000_000;
const MIN_RATE = 0;
const MAX_RATE = 100;
const MIN_TENURE_MONTHS = 1;
const MAX_TENURE_MONTHS = 600;

/**
 * @param {string | number} value
 */
export function parseAmountInput(value) {
  if (value === "" || value === null || value === undefined) return null;
  const cleaned = String(value).replace(/[^\d.]/g, "");
  if (!cleaned || cleaned === ".") return null;
  const num = Number(cleaned);
  return Number.isFinite(num) ? num : null;
}

/**
 * @param {number} amount
 */
export function formatAmountInput(amount) {
  if (!Number.isFinite(amount)) return "";
  const [intPart, decPart] = amount.toFixed(2).split(".");
  const withCommas = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return decPart === "00" ? withCommas : `${withCommas}.${decPart}`;
}

/**
 * @param {number} tenure
 * @param {'years' | 'months'} unit
 */
export function tenureToMonths(tenure, unit) {
  if (!Number.isFinite(tenure) || tenure <= 0) return 0;
  return unit === "years" ? Math.round(tenure * 12) : Math.round(tenure);
}

/**
 * @param {number} principal
 * @param {number} annualRate
 * @param {number} tenureMonths
 */
export function validateEmiInputs(principal, annualRate, tenureMonths) {
  if (!Number.isFinite(principal) || principal < MIN_PRINCIPAL || principal > MAX_PRINCIPAL) {
    return { valid: false, error: /** @type {EmiErrorCode} */ ("invalidPrincipal") };
  }
  if (!Number.isFinite(annualRate) || annualRate < MIN_RATE || annualRate > MAX_RATE) {
    return { valid: false, error: /** @type {EmiErrorCode} */ ("invalidRate") };
  }
  if (!Number.isFinite(tenureMonths) || tenureMonths < MIN_TENURE_MONTHS || tenureMonths > MAX_TENURE_MONTHS) {
    return { valid: false, error: /** @type {EmiErrorCode} */ ("invalidTenure") };
  }
  return { valid: true };
}

/**
 * @param {number} principal
 * @param {number} annualRate
 * @param {number} months
 * @param {number} emi
 */
function buildAmortizationSchedule(principal, annualRate, months, emi) {
  /** @type {{ month: number, emi: number, principalPaid: number, interestPaid: number, balance: number }[]} */
  const schedule = [];
  let balance = principal;
  const monthlyRate = annualRate / 12 / 100;

  for (let month = 1; month <= months; month += 1) {
    const interestPaid = monthlyRate === 0 ? 0 : balance * monthlyRate;
    let principalPaid;
    let payment;

    if (month === months) {
      principalPaid = balance;
      payment = principalPaid + interestPaid;
      balance = 0;
    } else {
      principalPaid = emi - interestPaid;
      payment = emi;
      balance = Math.max(0, balance - principalPaid);
    }

    schedule.push({
      month,
      emi: payment,
      principalPaid,
      interestPaid,
      balance,
    });
  }

  return schedule;
}

/**
 * @param {number} principal
 * @param {number} annualRate percent per year
 * @param {number} tenureMonths
 */
export function calculateEmi(principal, annualRate, tenureMonths) {
  const validation = validateEmiInputs(principal, annualRate, tenureMonths);
  if (!validation.valid) {
    return { ok: false, error: validation.error };
  }

  const months = Math.round(tenureMonths);
  let emi;
  let totalPayment;
  let totalInterest;

  if (annualRate === 0) {
    emi = principal / months;
    totalPayment = principal;
    totalInterest = 0;
  } else {
    const r = annualRate / 12 / 100;
    const factor = Math.pow(1 + r, months);
    emi = (principal * r * factor) / (factor - 1);
    totalPayment = emi * months;
    totalInterest = totalPayment - principal;
  }

  const schedule = buildAmortizationSchedule(principal, annualRate, months, emi);

  return {
    ok: true,
    emi,
    principal,
    totalInterest,
    totalPayment,
    interestPercent: totalPayment > 0 ? (totalInterest / totalPayment) * 100 : 0,
    tenureMonths: months,
    schedule,
  };
}

export const LOAN_PRESETS = {
  home: { principal: 5_000_000, rate: 8.5, tenure: 20, unit: /** @type {const} */ ("years") },
  car: { principal: 1_500_000, rate: 9, tenure: 5, unit: /** @type {const} */ ("years") },
  personal: { principal: 500_000, rate: 12, tenure: 3, unit: /** @type {const} */ ("years") },
  education: { principal: 800_000, rate: 10, tenure: 7, unit: /** @type {const} */ ("years") },
};

export const SLIDER_LIMITS = {
  principal: { min: 10_000, max: 50_000_000, step: 10_000 },
  rate: { min: 0, max: 30, step: 0.1 },
  tenureYears: { min: 1, max: 30, step: 1 },
  tenureMonths: { min: 6, max: 360, step: 1 },
};
